/**
 * Divides two values (string or number) with special handling for comma decimals and percentages.
 * - If both are numbers, returns their quotient as a number.
 * - If either is a string with a comma decimal, converts to number.
 * - If either is a percentage string (e.g. "12,5%"), strips %, converts to number, divides, and returns as string with %.
 * - Any other string is treated as 0.
 * - Division by zero returns 0 (or "0%" for percent).
 */
export function divideSmart(a: string | number, b: string | number): number | string {
  // Helper to parse a value to number, handling comma decimals and percentages
  const parseValue = (val: string | number): { num: number, isPercent: boolean } => {
    if (typeof val === 'number') return { num: val, isPercent: false };
    if (typeof val === 'string') {
      let str = val.trim();
      if (str.endsWith('%')) {
        str = str.slice(0, -1).replace(',', '.');
        const num = parseFloat(str);
        return { num: isNaN(num) ? 0 : num, isPercent: true };
      }
      // comma decimal
      if (/^-?\d+(,\d+)?$/.test(str)) {
        const num = parseFloat(str.replace(',', '.'));
        return { num: isNaN(num) ? 0 : num, isPercent: false };
      }
      // plain number
      const num = parseFloat(str);
      return { num: isNaN(num) ? 0 : num, isPercent: false };
    }
    return { num: 0, isPercent: false };
  };

  const aParsed = parseValue(a);
  const bParsed = parseValue(b);

  // Avoid division by zero
  if (bParsed.num === 0) {
    return aParsed.isPercent || bParsed.isPercent ? '0%' : 0;
  }

  // If either is percent, return quotient as percent string
  if (aParsed.isPercent || bParsed.isPercent) {
    const quotient = Math.round(aParsed.num / bParsed.num);
    return `${quotient}%`;
  }
  // Otherwise, return quotient as number
  return Math.round(aParsed.num / bParsed.num);
}
